import React, { useEffect, useState, useMemo } from "react";
import {
  PDFViewer,
  Page,
  Text,
  View,
  Document,
} from "@react-pdf/renderer";
import moment from "moment";
import axios from "axios";
import config from "../../../config";
import PrintHeaderAccount from "../../../Pdf/PrintHeaderAccount";
import { Spin } from "antd";
import PrintFooter from "../../../Pdf/PrintFooter";
import { saleReportStyles } from "../../../Pdf/pdfStyles";
import {
  PrintFormateNumber,
  parseReportAmount,
} from "../../../Pdf/PrintFormateNumber";

const COL = {
  accountTitle: 130,
  type: 56,
  email: 100,
  mobile: 85,
  amount: 78,
  balance: 118,
};

const TABLE_WIDTH =
  COL.accountTitle +
  COL.type +
  COL.email +
  COL.mobile +
  COL.amount +
  COL.balance;

const VOUCHER_MODE_LABELS = {
  CreditSale: "Credit Sale",
  CreditPurchase: "Credit Purchase",
  Payments: "Payment",
  Receipts: "Receipt",
  Expenses: "Expense",
  General: "General",
};

const fmtMoney = (val) => PrintFormateNumber(parseReportAmount(val), 0);

const formatBalance = (account) => {
  if (!account) return "—";
  const balance = parseReportAmount(account.Balance);
  const balanceType = account.BalanceType;
  if (balance === 0 && !balanceType) return "—";
  if (balanceType) {
    return `${PrintFormateNumber(balance, 0)} ${balanceType}`;
  }
  return PrintFormateNumber(balance, 0);
};

const accountTypeName = (account) =>
  account?.Account_Type_BE?.Name || "—";

const displayValue = (value) => {
  if (value == null) return "—";
  const text = String(value).trim();
  return text || "—";
};

function AccountSection({ title, account, amount }) {
  return (
    <View style={{ marginBottom: 10 }}>
      <Text
        style={[saleReportStyles.pagetitle, { fontSize: "9pt", marginBottom: 4 }]}
      >
        {title}
      </Text>
      <View style={[saleReportStyles.table, { width: TABLE_WIDTH }]}>
        <View style={saleReportStyles.tableHead}>
          <View style={[saleReportStyles.th, { width: COL.accountTitle }]}>
            <Text style={saleReportStyles.thValue}>Account Title</Text>
          </View>
          <View style={[saleReportStyles.th, { width: COL.type }]}>
            <Text style={saleReportStyles.thValue}>Type</Text>
          </View>
          <View style={[saleReportStyles.th, { width: COL.email }]}>
            <Text style={saleReportStyles.thValue}>Email</Text>
          </View>
          <View style={[saleReportStyles.th, { width: COL.mobile }]}>
            <Text style={saleReportStyles.thValue}>Mobile</Text>
          </View>
          <View
            style={[
              saleReportStyles.th,
              { width: COL.amount, textAlign: "right" },
            ]}
          >
            <Text style={saleReportStyles.thValue}>Amount</Text>
          </View>
          <View style={[saleReportStyles.th, { width: COL.balance }]}>
            <Text style={saleReportStyles.thValue}>
              Current Balance (After Entry)
            </Text>
          </View>
        </View>

        <View style={saleReportStyles.row}>
          <View style={[saleReportStyles.td, { width: COL.accountTitle }]}>
            <Text style={saleReportStyles.tdValue}>
              {displayValue(account?.Name)}
            </Text>
          </View>
          <View style={[saleReportStyles.td, { width: COL.type }]}>
            <Text style={saleReportStyles.tdValue}>
              {accountTypeName(account)}
            </Text>
          </View>
          <View style={[saleReportStyles.td, { width: COL.email }]}>
            <Text style={saleReportStyles.tdValue}>
              {displayValue(account?.Email_Address)}
            </Text>
          </View>
          <View style={[saleReportStyles.td, { width: COL.mobile }]}>
            <Text style={saleReportStyles.tdValue}>
              {displayValue(account?.Mobile_No)}
            </Text>
          </View>
          <View
            style={[
              saleReportStyles.td,
              { width: COL.amount, textAlign: "right" },
            ]}
          >
            <Text style={saleReportStyles.tdValue}>{fmtMoney(amount)}</Text>
          </View>
          <View style={[saleReportStyles.td, { width: COL.balance }]}>
            <Text style={saleReportStyles.tdValue}>
              {formatBalance(account)}
            </Text>
          </View>
        </View>
      </View>
    </View>
  );
}

const PrintVoucherList = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const voucherId = urlParams.get("voucher_id");

  const UserID = localStorage.getItem("ID");
  const AccessKey = localStorage.getItem("AccessKey");

  const [voucher, setVoucher] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Print Voucher";
    if (!UserID || !AccessKey || !voucherId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    axios({
      method: "post",
      url: config.base_url + "Voucher/GetVoucherByID",
      headers: { "Content-Type": "application/json" },
      data: {
        UserID,
        AccessKey,
        ID: voucherId,
      },
    })
      .then(function (response) {
        if (response.data.status_code == 1) {
          setVoucher(response.data);
        } else {
          setVoucher({});
        }
      })
      .catch(function () {
        setVoucher({});
      })
      .finally(function () {
        setLoading(false);
      });
  }, [UserID, AccessKey, voucherId]);

  const modeLabel =
    VOUCHER_MODE_LABELS[voucher.VoucherMode] ||
    voucher.VoucherMode ||
    "General";

  const voucherDate = useMemo(() => {
    if (voucher.Dated && moment(voucher.Dated).isValid()) {
      return moment(voucher.Dated).format(config.date_format);
    }
    return "—";
  }, [voucher.Dated]);

  const MyDocument = () => (
    <Document>
      <Page size="A4" style={saleReportStyles.page}>
        <PrintHeaderAccount compact />
        <View style={saleReportStyles.section}>
          <View style={saleReportStyles.titleBlock}>
            <Text style={saleReportStyles.pagetitle}>
              {modeLabel} Voucher
            </Text>
            <Text style={saleReportStyles.reportSubtitle}>
              Voucher No: {voucher.VoucherNo ?? voucherId}    Date: {voucherDate}
            </Text>
            {voucher.Description ? (
              <Text style={saleReportStyles.reportSubtitle}>
                Description: {voucher.Description}
              </Text>
            ) : null}
            {voucher.PaymentReference ? (
              <Text style={saleReportStyles.reportSubtitle}>
                Payment reference: {voucher.PaymentReference}
              </Text>
            ) : null}
          </View>

          <AccountSection
            title="Debit Account Details"
            account={voucher.DebitAccount}
            amount={voucher.Amount}
          />

          <AccountSection
            title="Credit Account Details"
            account={voucher.CreditAccount}
            amount={voucher.Amount}
          />

          <View
            style={[
              saleReportStyles.totalsRow,
              { width: TABLE_WIDTH, flexDirection: "row" },
            ]}
          >
            <View
              style={[
                saleReportStyles.th,
                {
                  width:
                    COL.accountTitle +
                    COL.type +
                    COL.email +
                    COL.mobile,
                  textAlign: "right",
                },
              ]}
            >
              <Text style={saleReportStyles.thValue}>Voucher Amount</Text>
            </View>
            <View
              style={[
                saleReportStyles.th,
                { width: COL.amount, textAlign: "right" },
              ]}
            >
              <Text style={saleReportStyles.thValue}>
                {fmtMoney(voucher.Amount)}
              </Text>
            </View>
            <View style={[saleReportStyles.th, { width: COL.balance }]}>
              <Text style={saleReportStyles.thValue}> </Text>
            </View>
          </View>
        </View>
        <PrintFooter fixed />
      </Page>
    </Document>
  );

  return (
    <div style={{ width: "100%", height: "100vh", overflow: "hidden" }}>
      {loading ? (
        <div
          style={{
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Spin />
        </div>
      ) : (
        <PDFViewer width="100%" height="100%">
          <MyDocument />
        </PDFViewer>
      )}
    </div>
  );
};

export default PrintVoucherList;
